import { cloneElement, useCallback, useEffect, useId, useRef, useState } from 'react';

import type { KeyboardEvent, ReactElement, ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';
import { useDismiss } from '@/shared/ui/useDismiss';
import { CheckIcon, ChevronRightIcon } from '@/shared/ui/icons';

/* Focus moves by walking the rendered items rather than by tracking an index in
   state — submenus mount and unmount underneath, and a DOM query always sees the
   list as it actually is.

   Direct children, plus the members of a directly-owned `role="group"` — which
   is the ARIA ownership model rather than a special case: a menu owns its items
   and its groups' items, and nothing deeper. `:scope >` alone skipped the shelf
   colour swatches entirely (a radio group is nested by definition), so arrow
   keys walked straight past them and Tab closed the menu — the control had no
   keyboard path at all. A submenu's items stay out of this: they live in their
   own nested `role="menu"`, not in a group. */
const ITEM_ROLES = '[role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"]';

function menuItems(container: HTMLElement | null): HTMLElement[] {
  if (!container) return [];
  const selector = [`:scope > :is(${ITEM_ROLES})`, `:scope > [role="group"] :is(${ITEM_ROLES})`]
    .map((scope) => `${scope}:not([aria-disabled="true"])`)
    .join(', ');
  return Array.from(container.querySelectorAll<HTMLElement>(selector));
}

function focusRelative(container: HTMLElement | null, delta: number) {
  const items = menuItems(container);
  if (items.length === 0) return;
  const active = document.activeElement as HTMLElement | null;
  const index = active ? items.indexOf(active) : -1;
  const next = index === -1 ? (delta > 0 ? 0 : items.length - 1) : (index + delta + items.length) % items.length;
  items[next]?.focus();
}

/* ------------------------------------------------------------------------- */

export type MenuProps = {
  /** The control that opens the menu. Cloned to receive the trigger wiring. */
  trigger: ReactElement<Record<string, unknown>>;
  /** Names the menu for assistive tech. */
  label: string;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  children: ReactNode;
  className?: string;
};

export function Menu({ trigger, label, placement = 'bottom-end', children, className }: MenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  const close = useCallback(() => setOpen(false), []);
  useDismiss({ open, onDismiss: close, refs: [anchorRef] });

  // Opening with the keyboard should land focus on the first item; opening with
  // the mouse shouldn't yank it, but a menu with nothing focused traps arrow
  // keys, so focus the list either way.
  useEffect(() => {
    if (!open) return;
    const first = menuItems(listRef.current)[0];
    first?.focus();
  }, [open]);

  // Returning focus to the trigger on close is what makes a menu usable by
  // keyboard — otherwise focus falls back to <body> and tab order restarts.
  //
  // Both ways out of an open menu need it, which is what this used to get wrong:
  // Escape restored, and *choosing an item* did not, so the one path a keyboard
  // user actually takes to get something done was the one that dropped them at
  // the top of the page. Pointer users are unaffected either way — focus follows
  // a click already, and `:focus-visible` will not ring a programmatic focus
  // whose last interaction was a pointer.
  const closeAndRestore = useCallback(() => {
    setOpen(false);
    const triggerEl = anchorRef.current?.querySelector<HTMLElement>('[aria-haspopup="menu"]');
    triggerEl?.focus();
  }, []);

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusRelative(listRef.current, 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusRelative(listRef.current, -1);
        break;
      case 'Home':
        event.preventDefault();
        menuItems(listRef.current)[0]?.focus();
        break;
      case 'End': {
        event.preventDefault();
        const items = menuItems(listRef.current);
        items[items.length - 1]?.focus();
        break;
      }
      case 'Escape':
        event.preventDefault();
        closeAndRestore();
        break;
      case 'Tab':
        // Tabbing out of a menu closes it rather than walking through it.
        close();
        break;
      default:
        break;
    }
  };

  /* cloneElement's second argument *replaces* same-named props, so a trigger
     arriving with its own onClick — analytics, a close-the-drawer side effect —
     would have it silently dropped: the menu still opens, nothing warns, and
     the handler's absence is invisible in review and in a test that only checks
     the menu. None of today's nine call sites passes one; this is here so the
     first one that does is not a debugging session. Theirs runs first, ours
     after, which is the order a caller would write by hand. */
  const own = trigger.props as {
    onClick?: (event: React.MouseEvent) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
  };

  const triggerProps = {
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    'aria-controls': open ? menuId : undefined,
    onClick: (event: React.MouseEvent) => {
      own.onClick?.(event);
      event.stopPropagation();
      setOpen((v) => !v);
    },
    onKeyDown: (event: React.KeyboardEvent) => {
      own.onKeyDown?.(event);
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setOpen(true);
      }
    },
  };

  return (
    <div className={cx('menu-anchor', className)} ref={anchorRef}>
      {cloneElement(trigger, triggerProps)}
      {open ? (
        <div
          ref={listRef}
          id={menuId}
          role="menu"
          aria-label={label}
          className={cx('menu', `menu--${placement}`)}
          onKeyDown={handleListKeyDown}
          /* The item's own handler has already run by the time this fires —
             `onSelect` first, then the close — so anything that opens a dialog
             or navigates still takes focus off the trigger afterwards. */
          onClick={closeAndRestore}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export type MenuItemProps = {
  icon?: ReactNode;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  /** Trailing text, e.g. a shortcut or a count. */
  detail?: ReactNode;
  /**
   * Present — `true` or `false` — makes this one of a mutually exclusive set:
   * the row becomes a `menuitemradio` carrying `aria-checked`, and takes a tick
   * rather than an icon. Absent is an ordinary command item. The distinction is
   * the whole point of the prop: a screen reader announcing "Title, menu item"
   * gives no way to hear which sort is currently in force.
   */
  selected?: boolean;
  /**
   * The same tick, the other claim: an independent toggle, `menuitemcheckbox`.
   * Kept separate from `selected` because the two say different things and only
   * one of them is true of a given menu. A video sits on several shelves at
   * once, so a shelf row that announced itself as a radio would be telling a
   * screen-reader user that choosing this one un-chooses the rest.
   *
   * Set at most one of `selected` and `checked`; `selected` wins if both.
   */
  checked?: boolean;
  onSelect?: () => void;
  children: ReactNode;
};

export function MenuItem({ icon, tone = 'default', disabled, detail, selected, checked, onSelect, children }: MenuItemProps) {
  const state = selected !== undefined ? selected : checked;
  const role =
    selected !== undefined ? 'menuitemradio' : checked !== undefined ? 'menuitemcheckbox' : 'menuitem';

  return (
    <button
      type="button"
      role={role}
      aria-checked={state}
      tabIndex={-1}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      className={cx('menu__item', tone === 'danger' && 'menu__item--danger')}
      onClick={() => {
        if (!disabled) onSelect?.();
      }}
    >
      {state !== undefined ? (
        /* The tick's slot is held whether or not it is filled, so the labels of
           a choice set stay in one column instead of stepping sideways as the
           selection moves. */
        <span className="menu__item-tick" aria-hidden>
          {state ? <CheckIcon size={14} /> : null}
        </span>
      ) : icon ? (
        <span className="menu__item-icon">{icon}</span>
      ) : null}
      <span className="menu__item-label">{children}</span>
      {detail ? <span className="menu__item-detail">{detail}</span> : null}
    </button>
  );
}

export function MenuSeparator() {
  return <div className="menu__sep" role="separator" />;
}

/* ------------------------------------------------------------------------- */

export type MenuSubmenuProps = {
  label: ReactNode;
  icon?: ReactNode;
  /** Names the nested menu; falls back to the label when it is a plain string. */
  ariaLabel?: string;
  children: ReactNode;
};

/** `Move to shelf ›` — opens on hover, focus, or ArrowRight. */
export function MenuSubmenu({ label, icon, ariaLabel, children }: MenuSubmenuProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const submenuId = useId();

  const name = ariaLabel ?? (typeof label === 'string' ? label : 'Submenu');

  // Opening by keyboard should carry focus into the panel; opening by hover
  // should not steal it. The flag records which happened, and the effect acts
  // once the panel has actually rendered — scheduling the focus off a rAF
  // instead would race the very next interaction.
  const focusOnOpenRef = useRef(false);

  /* Closing is deferred by a beat and cancelled if the pointer comes back.
     Tearing the panel down the instant a leave fires is brittle: the pointer
     crosses a sub-pixel seam on its way from the parent item into the panel, and
     an unmount mid-travel detaches the item before the click lands, silently
     dropping the selection. The grace period costs nothing perceptible and makes
     the menu behave under imprecise pointers. */
  const closeTimerRef = useRef<number | null>(null);

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelScheduledClose();
    closeTimerRef.current = window.setTimeout(() => {
      closeTimerRef.current = null;
      setOpen(false);
    }, 120);
  }, [cancelScheduledClose]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  useEffect(() => {
    if (!open || !focusOnOpenRef.current) return;
    focusOnOpenRef.current = false;
    menuItems(listRef.current)[0]?.focus();
  }, [open]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      cancelScheduledClose();
      if (open) {
        // Already open (hovered), so the effect above will not re-run.
        menuItems(listRef.current)[0]?.focus();
      } else {
        focusOnOpenRef.current = true;
        setOpen(true);
      }
    }
  };

  const handleListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      focusRelative(listRef.current, 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      event.stopPropagation();
      focusRelative(listRef.current, -1);
    } else if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      // Collapse back to the parent item rather than closing the whole menu.
      event.preventDefault();
      event.stopPropagation();
      cancelScheduledClose();
      setOpen(false);
      wrapRef.current?.focus();
    }
  };

  return (
    <div
      ref={wrapRef}
      role="menuitem"
      tabIndex={-1}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? submenuId : undefined}
      className={cx('menu__item', 'menu__item--submenu', open && 'is-open')}
      onMouseEnter={() => {
        cancelScheduledClose();
        setOpen(true);
      }}
      // A move *into* the panel is not a leave — the panel is a DOM descendant.
      onMouseLeave={(event) => {
        // relatedTarget is an EventTarget, not necessarily a Node — it is
        // `window` when the pointer leaves the document entirely, and
        // `contains()` throws on anything that is not a Node.
        const next = event.relatedTarget;
        if (next instanceof Node && wrapRef.current?.contains(next)) return;
        scheduleClose();
      }}
      /* Hover alone is not an affordance. The parent menu closes on click and
         this row lives inside it, so without this a click — or a tap, which
         fires an emulated mouseenter and then a click — opened the panel and
         immediately tore the whole menu down. Opening rather than toggling is
         deliberate: on touch the mouseenter has already opened it by the time
         the click lands, and a toggle would close it again. Clicks from inside
         the panel are real selections and must keep bubbling, or choosing a
         shelf would leave the menu standing open behind it. */
      onClick={(event) => {
        if (listRef.current?.contains(event.target as Node)) return;
        event.stopPropagation();
        cancelScheduledClose();
        setOpen(true);
      }}
      // Deliberately no onFocus-to-open. React bubbles focus events, so opening
      // here would re-fire the moment focus lands on a child — which both undoes
      // the ArrowLeft collapse below and remounts the panel mid-click. Keyboard
      // users open with ArrowRight/Enter/Space instead, which also stops a
      // submenu fanning out merely from arrowing past it.
      onKeyDown={handleKeyDown}
    >
      {icon ? <span className="menu__item-icon">{icon}</span> : null}
      <span className="menu__item-label">{label}</span>
      <ChevronRightIcon size={16} className="menu__item-chevron" />
      {open ? (
        <div
          ref={listRef}
          id={submenuId}
          role="menu"
          aria-label={name}
          className="menu menu--submenu"
          onKeyDown={handleListKeyDown}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
